import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';

const SubscriptionPlans = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [plans, setPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [addons, setAddons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Get dashboard path based on user role
    const getDashboardPath = () => {
        switch (user.role) {
            case 'STARTUP':
            case 'ENTREPRENEUR':
                return `/${tenantId}/startup/dashboard`;
            case 'INVESTOR':
                return `/${tenantId}/investor/dashboard`;
            case 'INVESTOR_NETWORK':
                return `/${tenantId}/investor-network/dashboard`;
            case 'ADMIN':
            case 'TENANT_ADMIN':
            default:
                return `/${tenantId}/dashboard`;
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Get applicable plan type based on user role
    const getApplicablePlanType = () => {
        switch (user.role) {
            case 'STARTUP':
            case 'ENTREPRENEUR':
                return 'STARTUP';
            case 'INVESTOR':
                return 'INVESTOR';
            case 'INVESTOR_NETWORK':
                return 'INVESTOR_NETWORK';
            default:
                return null;
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [plansData, subscriptionData, addonsData] = await Promise.all([
                subscriptionAPI.getPlans(),
                subscriptionAPI.getMySubscription().catch(() => null),
                subscriptionAPI.getAddons().catch(() => [])
            ]);

            // Filter plans based on user role - only show applicable plans
            const applicablePlanType = getApplicablePlanType();
            const filteredPlans = applicablePlanType
                ? plansData.filter(plan => plan.planType === applicablePlanType)
                : plansData;

            setPlans(filteredPlans);
            setCurrentSubscription(subscriptionData);

            // Filter addons based on user role - only show applicable addons
            const filteredAddons = addonsData.filter(addon => {
                if (!addon.applicablePlanTypes) return true;
                return addon.applicablePlanTypes.includes(applicablePlanType);
            });
            setAddons(filteredAddons);
        } catch (error) {
            console.error('Failed to load data:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = (plan) => {
        // Check if plan matches user role
        const rolePlanMap = {
            'STARTUP': 'STARTUP',
            'ENTREPRENEUR': 'STARTUP',
            'INVESTOR': 'INVESTOR',
            'INVESTOR_NETWORK': 'INVESTOR_NETWORK'
        };

        const expectedPlanType = rolePlanMap[user.role];
        if (plan.planType !== expectedPlanType) {
            setAlert({
                isOpen: true,
                title: 'Invalid Plan',
                message: `This plan is not available for your account type. Please select a ${expectedPlanType.replace('_', ' ')} plan.`,
                type: 'error'
            });
            return;
        }

        setSelectedPlan(plan);

        // Show different message based on whether user has active subscription
        const hasExisting = hasActiveSubscription();
        const currentPlanName = currentSubscription?.plan?.planName || '';
        const priceText = plan.basePrice === 0 ? 'Free' : `₹${plan.basePrice}/month`;

        let title, message;
        if (hasExisting) {
            const upgradeOrSwitch = isUpgrade(plan) ? 'Upgrade' : 'Switch';
            title = `${upgradeOrSwitch} to ${plan.planName}`;
            message = `You are currently on ${currentPlanName}. ${upgradeOrSwitch === 'Upgrade'
                ? `Upgrading to ${plan.planName} (${priceText}) will cancel your current subscription and start the new plan immediately.`
                : `Switching to ${plan.planName} (${priceText}) will cancel your current subscription. Your new plan will start immediately.`
                } Do you want to continue?`;
        } else {
            title = 'Subscribe to Plan';
            message = plan.basePrice === 0
                ? `Start your free ${plan.planName} subscription?`
                : `Are you sure you want to subscribe to ${plan.planName} for ${priceText}?`;
        }

        setConfirm({
            isOpen: true,
            title,
            message,
            type: hasExisting ? 'warning' : 'info',
            onConfirm: () => performSubscribe(plan.planCode, hasExisting)
        });
    };

    const performSubscribe = async (planCode, cancelExisting = false) => {
        setSubscribing(true);
        try {
            // If user has existing subscription, cancel it first
            if (cancelExisting) {
                await subscriptionAPI.cancelSubscription(true); // immediate cancellation
            }

            await subscriptionAPI.subscribe(planCode);

            const actionWord = cancelExisting ? 'upgraded' : 'created';
            setAlert({
                isOpen: true,
                title: 'Success',
                message: `Subscription ${actionWord} successfully!`,
                type: 'success'
            });
            loadData();
        } catch (error) {
            console.error('Subscription failed:', error);
            setAlert({
                isOpen: true,
                title: 'Error',
                message: error.message || 'Subscription failed',
                type: 'error'
            });
        } finally {
            setSubscribing(false);
        }
    };

    const getPlanFeatures = (plan) => {
        const features = [];

        // Storage feature
        if (plan.storageLimitGb > 0) {
            features.push(`${plan.storageLimitGb} GB Storage`);
        }

        // Investor links feature (for networks)
        if (plan.investorLinkLimit > 0) {
            features.push(`Up to ${plan.investorLinkLimit} Investor Links`);
        }

        // Plan type specific features
        if (plan.planType === 'STARTUP') {
            features.push('Upload & share documents');
            features.push('Investor data room access');
            features.push('Investment tracking');
            if (plan.storageLimitGb >= 25) {
                features.push('Priority support');
            }
            if (plan.storageLimitGb >= 100) {
                features.push('Dedicated account manager');
                features.push('Custom branding');
            }
        } else if (plan.planType === 'INVESTOR') {
            features.push('View shared documents');
            features.push('Portfolio tracking');
            features.push('Investment analytics');
            if (plan.basePrice > 0) {
                features.push('Priority deal access');
                features.push('Advanced reporting');
            }
        } else if (plan.planType === 'INVESTOR_NETWORK') {
            features.push('Manage investor users');
            features.push('Network investment tracking');
            features.push('Commitment management');
            if (plan.investorLinkLimit >= 100) {
                features.push('Priority support');
            }
            if (plan.investorLinkLimit >= 500) {
                features.push('Dedicated account manager');
                features.push('API access');
            }
        }

        return features;
    };

    const getRecommendedPlan = () => {
        if (!user.role) return null;
        if (user.role === 'STARTUP' || user.role === 'ENTREPRENEUR') {
            return plans.find(p => p.planCode === 'STARTUP_BASE');
        } else if (user.role === 'INVESTOR') {
            return plans.find(p => p.planCode === 'INVESTOR_FREE');
        } else if (user.role === 'INVESTOR_NETWORK') {
            return plans.find(p => p.planCode === 'NETWORK_BASE');
        }
        return null;
    };

    const isPlanActive = (planCode) => {
        return currentSubscription?.plan?.planCode === planCode &&
            currentSubscription?.status === 'ACTIVE';
    };

    const hasActiveSubscription = () => {
        return currentSubscription?.status === 'ACTIVE';
    };

    const isUpgrade = (plan) => {
        if (!hasActiveSubscription()) return false;
        const currentPrice = currentSubscription?.plan?.basePrice || 0;
        return plan.basePrice > currentPrice;
    };

    const isDowngrade = (plan) => {
        if (!hasActiveSubscription()) return false;
        const currentPrice = currentSubscription?.plan?.basePrice || 0;
        return plan.basePrice < currentPrice;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const recommendedPlan = getRecommendedPlan();

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate(getDashboardPath())}
                    className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Dashboard
                </button>
                <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
                <p className="text-gray-600 mt-2">Choose the perfect plan for your needs</p>
            </div>

            {/* Current Subscription Banner */}
            {currentSubscription?.status === 'ACTIVE' && (
                <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-green-800">
                                Active Subscription: {currentSubscription.plan?.planName}
                            </h3>
                            <p className="text-green-600 text-sm mt-1">
                                Renews on {new Date(currentSubscription.endDate).toLocaleDateString()}
                                ({currentSubscription.daysRemaining} days remaining)
                            </p>
                        </div>
                        <button
                            onClick={() => navigate(`/${tenantId}/my-subscription`)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            Manage Subscription
                        </button>
                    </div>
                </div>
            )}

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {plans.map((plan) => {
                    const isRecommended = recommendedPlan?.planCode === plan.planCode;
                    const isActive = isPlanActive(plan.planCode);
                    const features = getPlanFeatures(plan);

                    return (
                        <div
                            key={plan.id}
                            className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${isRecommended ? 'ring-4 ring-blue-500 transform scale-105' : ''
                                } ${isActive ? 'ring-4 ring-green-500' : ''}`}
                        >
                            {/* Recommended Badge */}
                            {isRecommended && (
                                <div className="absolute top-0 right-0 bg-blue-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                                    Recommended
                                </div>
                            )}

                            {/* Active Badge */}
                            {isActive && (
                                <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                                    Current Plan
                                </div>
                            )}

                            <div className="p-8">
                                <div className="text-center mb-6">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.planName}</h3>
                                    <div className="text-4xl font-bold text-blue-600">
                                        {plan.basePrice === 0 ? 'Free' : `₹${plan.basePrice}`}
                                    </div>
                                    <p className="text-gray-500 mt-1">per month</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    {features.map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="text-gray-700">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    onClick={() => handleSubscribe(plan)}
                                    disabled={isActive || subscribing}
                                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${isActive
                                            ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                            : hasActiveSubscription() && !isActive
                                                ? isUpgrade(plan)
                                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                    : 'bg-orange-600 text-white hover:bg-orange-700'
                                                : isRecommended
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                    : 'bg-gray-600 text-white hover:bg-gray-700'
                                        }`}
                                >
                                    {isActive
                                        ? 'Current Plan'
                                        : hasActiveSubscription()
                                            ? isUpgrade(plan) ? 'Upgrade Plan' : 'Switch Plan'
                                            : 'Subscribe Now'
                                    }
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Available Addons */}
            {addons.length > 0 && (
                <div className="mt-16">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Add-ons</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {addons.map((addon) => (
                            <div key={addon.id} className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{addon.addonName}</h3>
                                <p className="text-gray-600 text-sm mb-4">
                                    {addon.addonCategory === 'STORAGE'
                                        ? `Add ${addon.unitValue} GB of storage`
                                        : `Add ${addon.unitValue} investor links`}
                                </p>
                                <div className="text-2xl font-bold text-blue-600 mb-4">
                                    ₹{addon.unitPrice}
                                </div>
                                <button
                                    onClick={() => navigate(`/${tenantId}/purchase-addon`, { state: { addon } })}
                                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Purchase
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => { })}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
        </div>
    );
};

export default SubscriptionPlans;
